import { useCallback, useEffect, useState } from 'react';
import { useAdminExport } from '../../hooks/useAdminExport';
import type { ExportFormat, ExportType } from '../../hooks/useAdminExport';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import { getAdminAnalytics } from '../../api/admin.api';
import type { AdminAnalyticsData } from '../../api/admin.api';
import { getAdminExportUsersReportJson, downloadAdminExportUsersReport, type UserReportRow } from '../../api/admin.api';
import { getDashboardReportPrintHtml } from './dashboardReportPrintHtml';
import DashboardReportPrint from './DashboardReportPrint';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { ADMIN_PERMISSIONS } from '../../config/admin-permissions';
import { useAdminPolicy } from '../../hooks/useAdminPolicy';
import DangerActionModal from '../../components/DangerActionModal';
import './admin.css';

const isTelegramWebApp = typeof window !== 'undefined' && typeof (window as unknown as { Telegram?: { WebApp?: unknown } }).Telegram?.WebApp !== 'undefined';

type ExportPeriodPreset = '7d' | '30d' | 'all' | 'custom';

function getExportPeriodRange(preset: ExportPeriodPreset, customFrom: string, customTo: string): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  if (preset === 'all') return { from: '2000-01-01', to };
  if (preset === 'custom') {
    const firstDayMonth = (() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); })();
    return { from: customFrom || firstDayMonth, to: customTo || to };
  }
  const from = new Date(now);
  if (preset === '7d') from.setDate(from.getDate() - 6);
  else from.setDate(from.getDate() - 29);
  return { from: from.toISOString().slice(0, 10), to };
}

function getPeriodLabel(preset: ExportPeriodPreset, from: string, to: string): string {
  if (preset === 'all') return 'За всё время';
  if (preset === '7d') return 'Неделя';
  if (preset === '30d') return 'Месяц';
  return `с ${from} по ${to}`;
}

export default function AdminExport() {
  const { features } = useAdminConfig();
  const { loading, error, downloadByMonth, downloadByPeriod } = useAdminExport();
  const [format, setFormat] = useState<ExportFormat>('csv');
  useEffect(() => {
    if (features && features.exportXlsx === false) setFormat('csv');
  }, [features?.exportXlsx]);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [type, setType] = useState<ExportType>('all');

  // Dashboard report block
  const [exportPreset, setExportPreset] = useState<ExportPeriodPreset>('30d');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [reportData, setReportData] = useState<AdminAnalyticsData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const reportRange = getExportPeriodRange(exportPreset, exportFrom, exportTo);
  const customPeriodInvalid = Boolean(exportPreset === 'custom' && exportFrom && exportTo && exportFrom > exportTo);

  const handleExportPresetChange = useCallback((preset: ExportPeriodPreset) => {
    setExportPreset(preset);
    if (preset === 'custom') {
      const firstDay = (() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); })();
      const today = new Date().toISOString().slice(0, 10);
      setExportFrom((prev) => prev || firstDay);
      setExportTo((prev) => prev || today);
    }
  }, []);

  const loadReport = useCallback(() => {
    if (customPeriodInvalid) return;
    setReportError(null);
    setReportLoading(true);
    const isAll = exportPreset === 'all';
    const params = isAll ? { period: 'all' as const } : { from: reportRange.from, to: reportRange.to };
    getAdminAnalytics(params)
      .then(setReportData)
      .catch((e) => setReportError(e instanceof Error ? e.message : 'Ошибка загрузки'))
      .finally(() => setReportLoading(false));
  }, [exportPreset, reportRange.from, reportRange.to, customPeriodInvalid]);

  const handlePrintDashboard = useCallback(() => {
    if (!reportData) return;
    const periodLabel = getPeriodLabel(exportPreset, reportRange.from, reportRange.to);
    const printDoc = getDashboardReportPrintHtml(reportData, periodLabel);

    const blob = new Blob([printDoc], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const w = window.open(blobUrl, '_blank', 'width=900,height=1100,noopener=yes');
    if (w) {
      const cleanup = () => {
        try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
        try { w.close(); } catch { /* ignore */ }
      };
      w.addEventListener('afterprint', cleanup, { once: true });
      w.addEventListener('load', () => {
        w.setTimeout(() => {
          try {
            w.focus();
            w.print();
          } catch {
            cleanup();
          }
        }, 550);
      }, { once: true });
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.padding = '0';
    iframe.style.overflow = 'hidden';
    iframe.style.zIndex = '-9999';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';
    iframe.setAttribute('title', 'Печать отчёта');
    document.body.appendChild(iframe);
    
    const iframeWindow = iframe.contentWindow;
    if (!iframeWindow) {
      document.body.removeChild(iframe);
      try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
      return;
    }

    const cleanupIframe = () => {
      try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    };
    
    iframeWindow.addEventListener('afterprint', cleanupIframe);
    // В некоторых браузерах/webview afterprint может не вызваться — удаляем iframe через 2 минуты
    setTimeout(cleanupIframe, 120000);

    iframe.addEventListener('load', () => {
      iframeWindow.setTimeout(() => {
        try {
          iframeWindow.focus();
          iframeWindow.print();
        } catch {
          cleanupIframe();
        }
      }, 600);
    }, { once: true });
    iframe.srcdoc = printDoc;
  }, [reportData, exportPreset, reportRange.from, reportRange.to]);

  // Users report block
  const [usersReportData, setUsersReportData] = useState<UserReportRow[] | null>(null);
  const [usersReportLoading, setUsersReportLoading] = useState(false);
  const [usersReportError, setUsersReportError] = useState<string | null>(null);
  const [printUsersAfterLoad, setPrintUsersAfterLoad] = useState(false);

  const loadUsersReportAndPrint = useCallback(() => {
    setUsersReportError(null);
    setUsersReportLoading(true);
    setPrintUsersAfterLoad(true);
    getAdminExportUsersReportJson()
      .then(setUsersReportData)
      .catch((e) => {
        setUsersReportError(e instanceof Error ? e.message : 'Ошибка загрузки');
        setPrintUsersAfterLoad(false);
      })
      .finally(() => setUsersReportLoading(false));
  }, []);

  useEffect(() => {
    if (!printUsersAfterLoad || usersReportLoading) return;
    setPrintUsersAfterLoad(false);
    if (!usersReportData || usersReportData.length === 0) {
      setUsersReportError('Нет данных для печати');
      return;
    }
    const cleanup = () => {
      document.body.classList.remove('admin-print-users');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    document.body.classList.add('admin-print-users');
    window.print();
  }, [printUsersAfterLoad, usersReportData, usersReportLoading]);

  const [usersDownloading, setUsersDownloading] = useState<'xlsx' | null>(null);
  const { hasPermission } = useAdminPermissions();
  const policy = useAdminPolicy();
  const canExportCsv = hasPermission(ADMIN_PERMISSIONS.EXPORT_CSV) || hasPermission(ADMIN_PERMISSIONS.USERS_EXPORT_CSV);
  const canExportXlsx = hasPermission(ADMIN_PERMISSIONS.EXPORT_XLSX) || hasPermission(ADMIN_PERMISSIONS.USERS_EXPORT_XLSX);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<null | 'users-xlsx' | 'month' | 'period'>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const handleDownloadUsersXlsx = useCallback(async () => {
    try {
      setUsersReportError(null);
      setUsersDownloading('xlsx');
      const blob = await downloadAdminExportUsersReport('xlsx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setUsersReportError(e instanceof Error ? e.message : 'Ошибка скачивания');
    } finally {
      setUsersDownloading(null);
    }
  }, []);

  const handlePrintUsers = useCallback(() => {
    if (usersReportData && usersReportData.length > 0) {
      const cleanup = () => {
        document.body.classList.remove('admin-print-users');
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);
      document.body.classList.add('admin-print-users');
      window.print();
    } else {
      loadUsersReportAndPrint();
    }
  }, [usersReportData, loadUsersReportAndPrint]);

  const handleMonthDownload = () => {
    downloadByMonth(month);
  };

  const handlePeriodDownload = () => {
    if (!from || !to) return;
    downloadByPeriod({ from, to, format, type });
  };

  const runConfirmedAction = async (reason?: string) => {
    try {
      setConfirmError(null);
      setConfirmLoading(true);
      if (confirmAction === 'users-xlsx') {
        await handleDownloadUsersXlsx();
      } else if (confirmAction === 'month') {
        handleMonthDownload();
      } else if (confirmAction === 'period') {
        handlePeriodDownload();
      }
      setConfirmAction(null);
    } catch (e) {
      setConfirmError(e instanceof Error ? e.message : 'Не удалось выполнить действие');
    } finally {
      setConfirmLoading(false);
      void reason;
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-card" style={{ marginBottom: '12px' }}>
        {!policy.canExport ? 'Экспорт временно недоступен.' : !policy.canExportXlsx ? 'XLSX экспорт отключен в текущей конфигурации.' : 'Экспорт доступен.'}
      </div>
      {isTelegramWebApp && (
        <section className="admin-card admin-export-no-print" style={{ marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: 0 }}>
            На телефоне скачивание и печать могут не работать. Для экспорта и печати откройте админ-панель на компьютере (в браузере или в Telegram Desktop).
          </p>
        </section>
      )}
      {/* Dashboard report */}
      <section className="admin-card admin-export-no-print" style={{ marginBottom: '24px' }}>
        <div className="admin-card-title">Отчёт дашборда</div>
        <div className="admin-filters" style={{ flexWrap: 'wrap', gap: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <span id="export-period-label">Период:</span>
            <select
              aria-labelledby="export-period-label"
              value={exportPreset}
              onChange={(e) => handleExportPresetChange(e.target.value as ExportPeriodPreset)}
              className="admin-input"
              style={{ minWidth: '140px' }}
            >
              <option value="7d">Неделя</option>
              <option value="30d">Месяц</option>
              <option value="all">За всё время</option>
              <option value="custom">Свой период</option>
            </select>
          </label>
          {exportPreset === 'custom' && (
            <>
              <input type="date" aria-label="Дата начала периода" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="admin-input admin-input-date" title="Дата начала" />
              <input type="date" aria-label="Дата окончания периода" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="admin-input admin-input-date" title="Дата окончания" />
            </>
          )}
          <button type="button" onClick={loadReport} disabled={reportLoading || customPeriodInvalid} className="admin-btn admin-btn-primary" aria-busy={reportLoading ? true : undefined}>
            {reportLoading ? 'Загрузка…' : 'Загрузить данные'}
          </button>
          {reportData && (
            <>
              <button type="button" onClick={handlePrintDashboard} className="admin-btn admin-btn-secondary" aria-label="Печать отчёта дашборда">
                Печать
              </button>
              <button type="button" onClick={handlePrintDashboard} className="admin-btn admin-btn-secondary" aria-label="Скачать отчёт дашборда в PDF">
                Скачать PDF
              </button>
            </>
          )}
        </div>
        {customPeriodInvalid && <div className="admin-error" style={{ marginTop: '8px' }}>Дата начала не может быть позже даты окончания.</div>}
        {reportError && <div className="admin-error" style={{ marginTop: '8px' }}>{reportError}</div>}
        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '12px', marginBottom: 0 }}>
          На компьютере откройте админ-панель в браузере (Chrome, Edge) — диалог печати отобразится полностью.
        </p>
      </section>

      {/* Скрытый блок для печати (fallback); основной вывод — через отдельное окно с HTML из данных */}
      <div id="dashboard-report-print" className="dashboard-report-print-container" aria-hidden="true">
        {reportData ? (
          <DashboardReportPrint
            data={reportData}
            periodLabel={getPeriodLabel(exportPreset, reportRange.from, reportRange.to)}
          />
        ) : null}
      </div>

      {/* Основные экспорты */}
      <section className="admin-card admin-export-no-print" style={{ marginBottom: '24px' }}>
        <div className="admin-card-title">Основные экспорты</div>
        <div className="admin-filters" style={{ flexWrap: 'wrap', gap: '10px' }}>
          {features?.exportXlsx !== false && canExportXlsx && (
            <button type="button" onClick={() => setConfirmAction('users-xlsx')} disabled={usersDownloading !== null} className="admin-btn admin-btn-primary" aria-busy={usersDownloading === 'xlsx'} aria-label="Скачать отчёт по пользователям в XLSX">
              {usersDownloading === 'xlsx' ? 'Скачивание…' : 'Скачать XLSX'}
            </button>
          )}
          <button type="button" onClick={usersReportData?.length ? handlePrintUsers : loadUsersReportAndPrint} disabled={usersReportLoading} className="admin-btn admin-btn-secondary" aria-label={usersReportData?.length ? 'Печать таблицы пользователей' : 'Загрузить данные и открыть печать'}>
            {usersReportLoading ? 'Загрузка…' : usersReportData?.length ? 'Печать' : 'Загрузить и печать'}
          </button>
        </div>
        {usersReportError && <div className="admin-error" style={{ marginTop: '8px' }}>{usersReportError}</div>}
      </section>

      {/* Hidden table for users print */}
      <div id="users-report-print" style={{ display: 'none' }}>
        {usersReportData && usersReportData.length > 0 && (
          <div style={{ padding: '12mm', fontFamily: 'system-ui', fontSize: '11px', color: '#1a1a1a' }}>
            <h1 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Отчёт по пользователям</h1>
            <p style={{ margin: '0 0 12px 0', color: '#666' }}>Сначала новые. Добавлены источник, себестоимость, валовая прибыль и маржинальность.</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} role="table">
              <thead>
                <tr>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>ФИО</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Телефон</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Telegram</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Источник</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Дата рег.</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Оформлено</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Оплачено</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Сумма оформл.</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Сумма оплач.</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Себестоимость</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Валовая прибыль</th>
                  <th scope="col" style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Маржинальность</th>
                </tr>
              </thead>
              <tbody>
                {usersReportData.map((row, i) => (
                  <tr key={i}>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px' }}>{row.name || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px' }}>{row.phone || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px' }}>{row.telegram}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px' }}>{row.source || '—'}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px' }}>{row.createdAt}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.orderCount}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.orderCountPaid}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.totalSum.toLocaleString('ru-RU')}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.totalPaid.toLocaleString('ru-RU')}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(row.paidCogs || 0).toLocaleString('ru-RU')}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(row.grossProfit || 0).toLocaleString('ru-RU')}</td>
                    <td style={{ border: '1px solid #ddd', padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(row.grossMarginPercent || 0).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legacy exports */}
      <section className="admin-card admin-export-no-print" style={{ marginBottom: '24px' }}>
        <div className="admin-card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Legacy/расширенные экспорты</span>
          <button type="button" className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setLegacyOpen((v) => !v)}>
            {legacyOpen ? 'Свернуть' : 'Развернуть'}
          </button>
        </div>
        {!policy.canUseLegacyExports && (
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Для вашей роли legacy-экспорты недоступны.
          </p>
        )}
        {legacyOpen && policy.canUseLegacyExports && (
        <>
        <div className="admin-card-title">Экспорт по месяцу</div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
          Выберите месяц — по нему выгружаются пользователи и заказы.
        </p>
        <div className="admin-filters">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="admin-input"
            style={{ minWidth: '140px' }}
            title="Месяц для экспорта"
          />
          <button type="button" onClick={() => setConfirmAction('month')} disabled={loading || !canExportCsv} className="admin-btn admin-btn-primary">
            {loading ? 'Скачивание…' : 'Скачать CSV'}
          </button>
        </div>
        <div className="admin-card-title">Экспорт за период</div>
        <div className="admin-filters" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Дата начала
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="admin-input admin-input-date"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Дата окончания
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="admin-input admin-input-date"
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
              <input type="radio" checked={format === 'csv'} onChange={() => setFormat('csv')} />
              CSV
            </label>
            {features?.exportXlsx !== false && canExportXlsx && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                <input type="radio" checked={format === 'xlsx'} onChange={() => setFormat('xlsx')} />
                XLSX
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Тип:</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ExportType)}
              className="admin-input"
              style={{ minWidth: '160px' }}
            >
              <option value="all">Все</option>
              <option value="active">Только активные (заказ в периоде)</option>
              <option value="no_orders">Только без заказов</option>
              <option value="repeat">Только повторные (≥2 заказа)</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => setConfirmAction('period')}
            disabled={loading || !from || !to || (format === 'xlsx' ? !canExportXlsx : !canExportCsv)}
            className="admin-btn admin-btn-primary"
          >
            {loading ? 'Скачивание…' : `Скачать ${format.toUpperCase()}`}
          </button>
          {loading && (
            <div className="admin-export-progress" role="progressbar" aria-busy="true">
              <div className="admin-export-progress-bar" />
            </div>
          )}
        </div>
        </>
        )}
      </section>

      {error && <div className="admin-error admin-export-no-print" style={{ marginTop: '16px' }}>{error}</div>}
      <DangerActionModal
        open={confirmAction != null}
        title="Подтверждение экспорта"
        description="Экспорт может содержать чувствительные данные и создать нагрузку на сервер."
        entityLabel={confirmAction === 'users-xlsx' ? 'Users XLSX' : confirmAction === 'month' ? 'Legacy month CSV' : 'Legacy period export'}
        confirmationText="EXPORT"
        requireReason
        loading={confirmLoading}
        error={confirmError}
        onClose={() => {
          if (!confirmLoading) setConfirmAction(null);
        }}
        onConfirm={async ({ reason }) => {
          await runConfirmedAction(reason);
        }}
      />
    </div>
  );
}
